/**
 * When Home reloads on its own: after the app has sat in the background this
 * long, the same rule the native apps follow. Never on a tab switch inside the
 * app — the feed is cached for the life of the page so that a return to Home
 * picks up where the customer left off.
 */
export const HOME_FEED_BACKGROUND_REFRESH_AFTER_MS = 5 * 60 * 1000;

type Listener = () => void;

const listeners = new Set<Listener>();
let forgetFeed: Listener | null = null;
let hiddenAt: number | null = null;
let isTracking = false;

function handleVisibilityChange(): void {
  if (document.visibilityState === 'hidden') {
    hiddenAt = Date.now();
    return;
  }

  if (hiddenAt === null) return;
  const hiddenForMs = Date.now() - hiddenAt;
  hiddenAt = null;
  if (hiddenForMs < HOME_FEED_BACKGROUND_REFRESH_AFTER_MS) return;

  // Forgotten first, so a mounted Home that reacts below resolves against an
  // empty cache rather than finding the feed it is meant to replace.
  forgetFeed?.();
  listeners.forEach((listener) => listener());
}

/**
 * Starts watching for the app going into the background. Idempotent, and kept
 * running once started: a stay in the background while the customer is on
 * another tab still has to count when they come back to Home.
 *
 * `onLongBackground` drops the cached feed, so whichever Home renders next loads
 * the way a fresh page would. The latest one passed wins.
 */
export function startTrackingHomeFeedBackgrounding({
  onLongBackground,
}: {
  onLongBackground: Listener;
}): void {
  forgetFeed = onLongBackground;
  if (isTracking || typeof document === 'undefined') return;

  isTracking = true;
  if (document.visibilityState === 'hidden') hiddenAt = Date.now();
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

/**
 * Called when the app comes back from a long stay in the background, after the
 * cached feed has been dropped. For a Home that is on screen at the time.
 */
export function subscribeToHomeFeedBackgroundReturn(
  listener: Listener
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function resetHomeFeedBackgroundTrackingForTests(): void {
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }
  listeners.clear();
  forgetFeed = null;
  hiddenAt = null;
  isTracking = false;
}
